import { useEffect, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { useConfirmVerification, useMe, useResendVerification } from "@/lib/auth";
import { useErrorMessage } from "@/lib/error-message";
import { useT } from "@/lib/i18n";

/**
 * Email-verification confirmation landing page. Reached via the link
 * in the verification email; renders three states:
 *
 *   1. **pending** — auto-firing on mount with a spinner. The user
 *      doesn't click anything — the moment the page mounts we POST
 *      `/auth/email/verify/confirm` so the verification feels instant.
 *   2. **success** — "you're verified" + a CTA back home. On success
 *      we invalidate `useMe()` (handled in the hook) so any
 *      banner-dismissal logic on other pages sees the verified state.
 *   3. **error** — bad/expired/used token. CTA depends on whether
 *      the user is signed in: if they are, the in-page resend works;
 *      if not, point them at /login.
 *
 * The token comes from `?token=…`. A missing or malformed token
 * skips the network call and shows the error state immediately.
 */
export function VerifyEmailPage() {
  const [params] = useSearchParams();
  const t = useT();
  const { data: user } = useMe();
  const confirm = useConfirmVerification();
  const resend = useResendVerification();
  const errorMessage = useErrorMessage();
  const token = params.get("token") ?? "";
  const validToken = /^[a-f0-9]{64}$/.test(token);

  // Fire once on mount. `useRef` guard prevents StrictMode's double-
  // invoke from sending the request twice — the server is idempotent
  // (second call would 400 with "already used") but the UI would
  // briefly flash "error" before settling. Cheaper to just guard it.
  const fired = useRef(false);
  useEffect(() => {
    if (!validToken || fired.current) return;
    fired.current = true;
    confirm.mutate({ token });
    // `confirm` from React Query is stable across renders; depending
    // on it would re-run the effect but it doesn't change the gate.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validToken, token]);

  let state: "pending" | "success" | "error";
  if (!validToken || confirm.isError) state = "error";
  else if (confirm.isSuccess) state = "success";
  else state = "pending";

  return (
    <div className="flex min-h-dvh w-full items-stretch justify-center bg-bg">
      <div className="flex w-full max-w-[440px] flex-col px-7 pt-12 pb-8">
        <div className="mb-12">
          <Logo className="size-12" />
        </div>

        {state === "pending" && (
          <>
            <h1 className="mb-2.5 text-[32px] font-bold leading-[1.1] tracking-[-0.03em]">
              {t("verify.pending_title")}
            </h1>
            <p className="text-[15px] leading-[1.5] text-text-3">{t("verify.pending_body")}</p>
          </>
        )}

        {state === "success" && (
          <>
            <h1 className="mb-2.5 text-[32px] font-bold leading-[1.1] tracking-[-0.03em]">
              {t("verify.success_title")}
            </h1>
            <p className="mb-9 text-[15px] leading-[1.5] text-text-3">{t("verify.success_body")}</p>
            <Link
              to="/"
              className="inline-flex h-12 items-center justify-center rounded-[10px] bg-[color:var(--accent)] px-6 text-[15px] font-semibold text-white"
            >
              {t("verify.success_cta")}
            </Link>
          </>
        )}

        {state === "error" && (
          <>
            <h1 className="mb-2.5 text-[32px] font-bold leading-[1.1] tracking-[-0.03em]">{t("verify.error_title")}</h1>
            <p className="mb-9 text-[15px] leading-[1.5] text-text-3">{t("verify.error_body")}</p>

            {user ? (
              // Signed in — the resend endpoint is auth-gated so this
              // works directly. Show inline status feedback (sent /
              // error / nothing) so the user knows their click did
              // something.
              <>
                <Button type="button" size="block" onClick={() => resend.mutate()} disabled={resend.isPending}>
                  {t("verify.resend")}
                </Button>
                {resend.isSuccess && (
                  <p className="mt-3 text-[13px] text-[color:var(--good,#047857)]" role="status">
                    {t("verify.resend_sent")}
                  </p>
                )}
                {resend.isError && (
                  <p className="mt-3 text-[13px] text-[color:var(--again)]" role="alert">
                    {/* Surface the specific error code (e.g. 429
                        rate-limit copy with the seconds-to-wait
                        interpolated) instead of a generic "couldn't
                        send" — gives the user actionable detail. */}
                    {errorMessage(resend.error) ?? t("verify.resend_error")}
                  </p>
                )}
              </>
            ) : (
              // Not signed in — can't resend without auth. Point them
              // at login and they can use the in-app banner / Settings
              // resend later.
              <Link
                to="/login"
                className="inline-flex h-12 items-center justify-center rounded-[10px] bg-[color:var(--accent)] px-6 text-[15px] font-semibold text-white"
              >
                {t("verify.error_cta")}
              </Link>
            )}
          </>
        )}
      </div>
    </div>
  );
}
